<template>
    <div v-show="isLoaded" class="tab-pane active">
        <div v-show="!isListEmpty" class="profiles-with-navigation no-print">
            <div class="common-nav-wrap">
                <Simplebar class="with-border-track">
                    <div class="common-nav">
                        <Sidebar
                            :parent-name="ROUTE_NAMES.SENSORS__RESIDENT_PROFILES"
                            :list="mappedResidentProfiles"
                            :current-id="currentResidentProfileId"
                            :search-placeholder="$t('sensors.residentProfilesTab.searchPlaceholder')"
                            :add-new-text="$t('sensors.residentProfilesTab.addNewResidentProfile')"
                            @delete="deleteById"
                        />
                    </div>
                </Simplebar>
            </div>
            <div v-if="isLoaded" class="component-with-nav-body resident-profile">
                <ResidentProfilesForm v-model="residentProfile" :forbidden-names="forbiddenNames" />
                <ResidentProfilesTable
                    v-model="sensors"
                    :sensor-names="sensorNames"
                    :sensor-id="residentProfile.sensor_profile_id"
                />
            </div>
        </div>
        <div v-if="isListEmpty" class="data-empty">
            <h3>{{ $t('sensors.residentProfilesTab.empty') }}</h3>
        </div>
        <div class="print-only">
            <h4 v-if="showPrintKeyword">{{ $t('print.search', { val: searchKeyword }) }}</h4>
            <table
                v-if="residentProfiles && residentProfiles.length > 0"
                class="table print-resident-profiles"
            >
                <thead>
                    <th v-text="$t('sensors.residentProfilesTab.name')"></th>
                    <th v-text="$t('sensors.residentProfilesTab.description')"></th>
                    <th v-text="$t('sensors.residentProfilesTab.listenIn')"></th>
                    <th v-text="$t('sensors.residentProfilesTab.enabledTimeWindow')"></th>
                    <th v-text="$t('sensors.residentProfilesTab.fromPlaceholder') + ' (hh:mm)'"></th>
                    <th v-text="$t('sensors.residentProfilesTab.toPlaceholder') + ' (hh:mm)'"></th>
                    <th v-text="$t('sensors.residentProfilesTab.sensors')"></th>
                </thead>
                <tbody>
                    <tr v-for="(profile, index) in filteredResidentProfiles" :key="index">
                        <td v-text="profile.name"></td>
                        <td v-text="profile.description"></td>
                        <td>
                            <i v-if="profile.listen_in" class="icon-checkmark"></i>
                            <i v-else class="icon-close"></i>
                        </td>
                        <td>
                            <i v-if="profile.time_window_enabled" class="icon-checkmark"></i>
                            <i v-else class="icon-close"></i>
                        </td>
                        <td
                            v-text="
                                profile.time_window_enabled && profile.time_window_from
                                    ? formatTime(profile.time_window_from.substring(0, 5))
                                    : ''
                            "
                        ></td>
                        <td
                            v-text="
                                profile.time_window_enabled && profile.time_window_to
                                    ? formatTime(profile.time_window_to.substring(0, 5))
                                    : ''
                            "
                        ></td>
                        <td>
                            <div
                                v-for="(sensor, index2) in profile.sensors"
                                :key="index2"
                                class="print-sensor"
                            >
                                <span v-text="sensor.name"></span>
                                <span
                                    >({{
                                        $t(
                                            'sensors.residentProfilesTab.sensor' +
                                                (sensor.enabled ? 'Enabled' : 'Disabled')
                                        )
                                    }}<template v-if="sensor.enabled"
                                        >, delay: {{ formatSensorTime(sensor.delay_time) }}, duration:
                                        {{ formatSensorTime(sensor.duration_time) }}</template
                                    >)</span
                                >
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script type="text/babel">
import Sidebar from '../common/Sidebar';
import ResidentProfilesForm from './resident-profiles/ResidentProfilesForm';
import ResidentProfilesTable from './resident-profiles/ResidentProfilesTable';
import fixingEditForm from '../common/fixingEditForm';
import mappingState from '../common/mappingState';
import { mapState, mapMutations } from 'vuex';
import orderBy from 'lodash-es/orderBy';
import Simplebar from 'simplebar-vue';
import {
    resourceResidentProfilesWithSensors,
    resourceResidentProfile,
    resourceSensorNames,
} from '../../api/resources';
import { getCancelToken } from '../../api';
import { errorHandler, ifIsNotCanceled, formatTime } from '../common/helpers';
import { deleteHandler } from '../common/deleteHandler';
import { SUCCESS, ERROR, ROUTE_NAMES, USER_TYPES } from '../../constants';

export default {
    components: {
        Sidebar,
        Simplebar,
        ResidentProfilesForm,
        ResidentProfilesTable,
    },

    mixins: [fixingEditForm, mappingState],
    constants: {
        ROUTE_NAMES: ROUTE_NAMES,
    },

    data() {
        return {
            residentProfile: {
                sensor_profile_id: '',
                name: '',
                description: '',
                listen_in: false,
                time_window_enabled: false,
                time_window_from: '',
                time_window_to: '',
                sensors: [],
            },
            residentProfiles: [],
            emptyType: {
                value: '',
                name: this.$t('sensors.residentProfilesTab.emptyTypeName'),
            },
            sensors: [
                {
                    no: 1,
                    type: '',
                    enabled: false,
                    delayTime: { HH: '', MM: '', SS: '' },
                    durationTime: { HH: '', MM: '', SS: '' },
                },
                {
                    no: 2,
                    type: '',
                    enabled: false,
                    delayTime: { HH: '', MM: '', SS: '' },
                    durationTime: { HH: '', MM: '', SS: '' },
                },
                {
                    no: 3,
                    type: '',
                    enabled: false,
                    delayTime: { HH: '', MM: '', SS: '' },
                    durationTime: { HH: '', MM: '', SS: '' },
                },
                {
                    no: 4,
                    type: '',
                    enabled: false,
                    delayTime: { HH: '', MM: '', SS: '' },
                    durationTime: { HH: '', MM: '', SS: '' },
                },
            ],
            sensorNames: [],
            currentResidentProfileId: null,
            isListEmpty: true,
            isLoaded: false,
            showPrintKeyword: false,
            incorrectAA: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['isFormValid', 'isTableValid', 'unsavedChanges']),

        filteredResidentProfiles() {
            return this.showPrintKeyword
                ? this.residentProfiles.filter(item => item.name.toLowerCase().includes(this.searchKeyword))
                : this.residentProfiles;
        },

        /**
         * Unifies array of resident profiles for proper render in sidebar
         * @returns {Array|{id, name, isUsed: *}[]}
         */
        mappedResidentProfiles() {
            return this.residentProfiles.map(i => ({
                id: i.sensor_profile_id,
                name: i.name,
                isUsed: i.used_in_location_profile,
            }));
        },

        forbiddenNames() {
            let spId = this.residentProfile.sensor_profile_id;
            return spId
                ? this.residentProfiles.filter(i => i.sensor_profile_id !== Number(spId))
                : this.residentProfiles;
        },
    },

    watch: {
        residentProfile: {
            handler(list) {
                this.setUnsavedChanges(true);
                let keyword = this.searchKeyword;
                this.showPrintKeyword =
                    keyword && list.some(item => item.name.toLowerCase().includes(keyword));
            },
            deep: true,
        },

        sensors: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },

        $route(to) {
            this.setMode(to);
        },

        searchKeyword() {
            let keyword = this.searchKeyword;
            this.showPrintKeyword =
                keyword && this.residentProfiles.some(item => item.name.toLowerCase().includes(keyword));
        },
    },

    created() {
        this.showLoading(true);
        // Event listener for edit form styles
        window.addEventListener('resize', this.checkHeightOnResize);
        this.updateData(() => {
            this.undo();
            this.showLoading(false);
            return Promise.resolve();
        });
        return this.loadData();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.checkHeightOnResize);
        this.setFormValid(true);
        this.setTableValid(true);
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        formatTime,

        loadData() {
            this.incorrectAA = false;
            return this.loadResidentProfiles()
                .then(this.loadSensorNames)
                .then(() => {
                    if (this.userType === USER_TYPES.SUPER_USER && this.incorrectAA) {
                        this.popup({
                            type: ERROR,
                            header: this.$t('common.error'),
                            text: this.$t('error.common.forbidden'),
                        });
                        errorHandler(403, ROUTE_NAMES.SENSORS__ROOM_PROFILES);
                    } else {
                        this.setMode(this.$route);
                    }
                    this.isLoaded = true;
                    this.showLoading(false);
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            errorHandler(response.status, ROUTE_NAMES.SENSORS__RESIDENT_PROFILES);
                            this.isLoaded = true;
                            return response;
                        },
                    })
                );
        },

        save() {
            // "p" property for unit test
            let p = Promise.resolve();
            this.showLoading(true);
            this.prepareSensorsForSave();

            if (this.isFormValid && this.isTableValid) {
                if (!this.residentProfile.time_window_enabled) {
                    this.residentProfile.time_window_from = null;
                    this.residentProfile.time_window_to = null;
                }
                if (this.residentProfile.time_window_from && this.residentProfile.time_window_to) {
                    this.residentProfile.time_window_from = (
                        '0' + this.residentProfile.time_window_from
                    ).slice(-5);
                    this.residentProfile.time_window_to = ('0' + this.residentProfile.time_window_to).slice(
                        -5
                    );
                }

                this.residentProfile.name = this.residentProfile.name.trim();
                this.residentProfile.description = this.residentProfile.description.trim();
                p = resourceResidentProfile({
                    method: 'post',
                    id: this.residentProfile.sensor_profile_id,
                    data: JSON.stringify(this.residentProfile),
                })
                    .then(response => {
                        let jsonData = response.data;
                        return this.loadResidentProfiles().then(() => {
                            if (
                                this.$router.currentRoute.name ===
                                    ROUTE_NAMES.SENSORS__RESIDENT_PROFILES__EDIT ||
                                this.$router.currentRoute.name === ROUTE_NAMES.SENSORS__RESIDENT_PROFILES__ADD
                            ) {
                                this.showLoading(false);
                                if (!this.residentProfile.sensor_profile_id) {
                                    if (jsonData && jsonData.sensor_profile_id) {
                                        this.setUnsavedChanges(false);
                                        // redirect to newly added
                                        this.$nextTick(() => {
                                            this.$router.replace({
                                                name: ROUTE_NAMES.SENSORS__RESIDENT_PROFILES__EDIT,
                                                params: {
                                                    id: jsonData.sensor_profile_id,
                                                },
                                            });
                                        });
                                    }
                                } else {
                                    this.resetSensors();
                                    this.setSensors();
                                    this.$nextTick(() => {
                                        this.setUnsavedChanges(false);
                                    });
                                }
                                this.popup({
                                    type: SUCCESS,
                                    header: this.$t('common.success'),
                                    text: this.$t('common.saved'),
                                });
                            }
                        });
                    })
                    .catch(response => {
                        this.showLoading(false);
                        if (response.data && response.data.error) {
                            this.popup({
                                type: ERROR,
                                header: this.$t('common.error'),
                                text: this.$t(response.data.error),
                            });
                        }
                        if (
                            response.status === 404 ||
                            response.status === 409 ||
                            (response.status === 400 &&
                                response.bodyText &&
                                response.bodyText.includes('limit of') &&
                                response.bodyText.includes('reached'))
                        ) {
                            return this.loadResidentProfiles().then(() => {
                                this.showLoading(false);
                                this.setUnsavedChanges(false);
                                this.$nextTick(() => {
                                    this.$router.replace({
                                        name: ROUTE_NAMES.SENSORS__RESIDENT_PROFILES,
                                    });
                                });
                            });
                        }
                        return Promise.resolve();
                    });
            }

            return p;
        },

        loadResidentProfiles() {
            return resourceResidentProfilesWithSensors({
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.residentProfiles = response.data ? orderBy(response.data, 'name') : [];
                    this.isListEmpty = !this.residentProfiles.length;
                    if (!this.isListEmpty && this.residentProfiles[0].assignmentarea_id !== this.userAreaId) {
                        this.incorrectAA = true;
                    }
                })
                .catch(response => {
                    this.isListEmpty = true;

                    return Promise.reject(response);
                });
        },

        loadSensorNames() {
            return resourceSensorNames({ cancelToken: this.cancelToken.token })
                .then(response => {
                    let list = [this.emptyType];
                    if (response.data) {
                        list = list.concat(
                            response.data.map(function(item) {
                                return {
                                    value: item.sensor_name_id,
                                    name: item.name,
                                };
                            })
                        );
                    }
                    this.sensorNames = list;
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response =>
                            errorHandler(response.status, ROUTE_NAMES.SENSORS__RESIDENT_PROFILES),
                    })
                );
        },

        prepareSensorsForSave() {
            let sensors = [];
            this.sensors.forEach((sensor, i) => {
                let delayTime = 0;
                let durationTime = 0;

                if (sensor.enabled) {
                    delayTime = this.convertTimeToSeconds(
                        sensor.delayTime.HH,
                        sensor.delayTime.MM,
                        sensor.delayTime.SS
                    );

                    durationTime = this.convertTimeToSeconds(
                        sensor.durationTime.HH,
                        sensor.durationTime.MM,
                        sensor.durationTime.SS
                    );
                } else if (this.residentProfile.sensors && this.residentProfile.sensors[i]) {
                    delayTime = this.residentProfile.sensors[i].delay_time;
                    durationTime = this.residentProfile.sensors[i].duration_time;
                }

                if (sensor.type) {
                    sensors.push({
                        sensor_name_id: sensor.type,
                        enabled: sensor.enabled,
                        delay_time: delayTime,
                        duration_time: durationTime,
                    });
                }
            });
            this.residentProfile.sensors = sensors.length ? sensors : null;
        },

        setCurrentResidentProfile(id) {
            let resident = this.residentProfiles.find(i => i.sensor_profile_id === Number(id));
            if (resident) {
                this.residentProfile = { ...resident };
                this.revertTimeToHHMM();
                this.$nextTick(() => {
                    this.setUnsavedChanges(false);
                });
            } else {
                this.popup({
                    type: ERROR,
                    header: this.$t('common.error'),
                    text: this.$t('sensors.residentProfilesTab.notFound'),
                });
                errorHandler(404, ROUTE_NAMES.SENSORS__RESIDENT_PROFILES);
            }
        },

        setEmptyResidentProfile() {
            this.residentProfile = {
                name: '',
                description: '',
                listen_in: false,
                time_window_enabled: false,
                time_window_from: '',
                time_window_to: '',
                sensors: [],
            };
        },

        setSensors() {
            if (this.residentProfile.sensors) {
                this.residentProfile.sensors.forEach((item, i) => {
                    let delayTime = this.convertTimeToHHMMSS(item.delay_time);
                    let durationTime = this.convertTimeToHHMMSS(item.duration_time);
                    this.sensors[i].type = item.sensor_name_id;
                    this.sensors[i].enabled = item.enabled;

                    // Couldn't assign directly because of binding
                    this.sensors[i].delayTime.HH = delayTime.HH;
                    this.sensors[i].delayTime.MM = delayTime.MM;
                    this.sensors[i].delayTime.SS = delayTime.SS;
                    this.sensors[i].durationTime.HH = durationTime.HH;
                    this.sensors[i].durationTime.MM = durationTime.MM;
                    this.sensors[i].durationTime.SS = durationTime.SS;
                });
                this.$nextTick(() => {
                    this.setUnsavedChanges(false);
                });
            }
        },

        resetSensors() {
            this.sensors.forEach(sensor => {
                sensor.type = '';
                sensor.enabled = false;
                sensor.delayTime.HH = '';
                sensor.delayTime.MM = '';
                sensor.delayTime.SS = '';
                sensor.durationTime.HH = '';
                sensor.durationTime.MM = '';
                sensor.durationTime.SS = '';
            });
        },

        revertTimeToHHMM() {
            let dateFrom;
            let dateTo;
            if (this.residentProfile.time_window_from && this.residentProfile.time_window_to) {
                dateFrom = this.residentProfile.time_window_from.slice(0, 5);
                dateTo = this.residentProfile.time_window_to.slice(0, 5);

                this.residentProfile.time_window_from = dateFrom;
                this.residentProfile.time_window_to = dateTo;
            }
        },

        convertTimeToSeconds(h, m, s) {
            let seconds;
            h = Number(h);
            m = Number(m);
            s = Number(s);

            h = isNaN(h) ? 0 : h;
            m = isNaN(m) ? 0 : m;
            s = isNaN(s) ? 0 : s;

            h = h >= 0 && h <= 24 ? h : 0;
            m = m >= 0 && m <= 59 ? m : 0;
            s = s >= 0 && s <= 59 ? s : 0;

            seconds = h * 60 * 60 + m * 60 + s;
            if (seconds > 24 * 60 * 60) {
                seconds = 0;
            }

            return seconds;
        },

        convertTimeToHHMMSS(seconds) {
            let date = new Date(seconds * 1000);

            return {
                HH: ('0' + (date.getUTCDate() > 1 ? 24 : date.getUTCHours())).slice(-2),
                MM: ('0' + date.getUTCMinutes()).slice(-2),
                SS: ('0' + date.getUTCSeconds()).slice(-2),
            };
        },

        isMaximum() {
            return this.residentProfiles.length >= 16;
        },

        deleteById(id) {
            let deletedProfile = this.residentProfiles.find(item => item.sensor_profile_id === id);
            let confirmPart = {
                confirmTitle: this.$t('sensors.residentProfilesTab.profiles.removingProfileTitle'),
                confirmBody: this.$t('sensors.residentProfilesTab.profiles.removingProfile', {
                    name: deletedProfile ? deletedProfile.name : '',
                }),
            };
            return deleteHandler(
                resourceResidentProfilesWithSensors,
                id,
                this.loadResidentProfiles,
                ROUTE_NAMES.SENSORS__RESIDENT_PROFILES,
                confirmPart
            );
        },

        initAdding() {
            this.setEmptyResidentProfile();
            this.setAddingButtons({
                cancel: () => {
                    this.setUnsavedChanges(false);
                    this.$nextTick(() => {
                        this.$router.push({ name: ROUTE_NAMES.SENSORS__RESIDENT_PROFILES });
                    });
                },
                save: () => {
                    this.save();
                },
                saveDisabled: () => !this.unsavedChanges || !this.isFormValid || !this.isTableValid,
            });
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        initEditing(id) {
            this.setCurrentResidentProfile(id);
            this.setEditingButtons({
                add: () =>
                    this.isMaximum()
                        ? null
                        : this.$router.push({
                              name: ROUTE_NAMES.SENSORS__RESIDENT_PROFILES__ADD,
                          }),
                addDisabled: () => this.isMaximum(),
                undo: () => this.undo(),
                wasChanged: () => !this.unsavedChanges,
                save: () => {
                    this.save();
                },
                saveDisabled: () => !this.unsavedChanges || !this.isFormValid || !this.isTableValid,
            });
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        formatSensorTime(val) {
            if (val && val > 0) {
                let time = this.convertTimeToHHMMSS(val);
                let parts = [time.HH, time.MM, time.SS];

                return parts.join(':');
            }

            return '-';
        },

        setMode(route) {
            if (route.name === ROUTE_NAMES.SENSORS__RESIDENT_PROFILES) {
                if (this.residentProfiles.length) {
                    let firstId = this.residentProfiles[0].sensor_profile_id;
                    this.$router.replace({
                        name: ROUTE_NAMES.SENSORS__RESIDENT_PROFILES__EDIT,
                        params: { id: firstId },
                    });
                    this.initEditing(firstId);
                } else {
                    this.configButton({
                        buttonName: 'addButton',
                        action: () =>
                            this.$router.push({
                                name: ROUTE_NAMES.SENSORS__RESIDENT_PROFILES__ADD,
                            }),
                    });
                    this.isListEmpty = true;
                }
            } else if (route.name === ROUTE_NAMES.SENSORS__RESIDENT_PROFILES__ADD) {
                this.isListEmpty = false;
                this.currentResidentProfileId = null;
                this.initAdding();
            } else {
                if (
                    this.residentProfiles.length &&
                    this.residentProfiles.some(s => s.sensor_profile_id === Number(route.params.id))
                ) {
                    this.isListEmpty = false;
                    this.currentResidentProfileId = Number(route.params.id);
                    this.initEditing(this.currentResidentProfileId);
                } else {
                    this.$router.replace({
                        name: ROUTE_NAMES.SENSORS__RESIDENT_PROFILES,
                    });
                }
            }
            this.resetSensors();
            this.$nextTick(this.setSensors);
            this.$nextTick(() => {
                this.checkHeightOnResize();
            });
        },

        undo() {
            let id = this.residentProfile.sensor_profile_id;
            if (id) {
                this.resetSensors();
                this.setCurrentResidentProfile(this.residentProfile.sensor_profile_id);
                this.$nextTick(() => {
                    this.setSensors();
                    this.setUnsavedChanges(false);
                });
            } else {
                this.$router.push({ name: ROUTE_NAMES.SENSORS__RESIDENT_PROFILES });
            }
        },
    },
};
</script>
